import { useEffect, useState } from "react";
import api, { fmtUSD, fmtQty } from "@/lib/api";
import { Panel, Table, Metric, Empty, StatusBadge, Money } from "@/components/terminal";

export default function TaxLedger() {
  const [events, setEvents] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.get("/tax/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/tax/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  if (!events || !summary) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="tax-ledger-page">
      <h2 className="font-display text-xl font-bold">Tax Event Ledger</h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Metric label="Estimated Tax Liability" value={fmtUSD(summary.estimated_tax_liability)} tone={summary.estimated_tax_liability > 0 ? "neg" : undefined} testId="tax-liability" />
        <Metric label="Tax Reserve Balance" value={fmtUSD(summary.tax_reserve_balance)} testId="tax-reserve-balance" />
        <Metric label="Reserve Funded" value={`${summary.tax_reserve_funded_pct.toFixed(0)}%`} tone={summary.underfunded ? "neg" : "pos"} testId="tax-funded-pct" />
        <Metric label="Realized Gains YTD" value={fmtUSD(summary.realized_gains_ytd)} tone="pos" testId="tax-gains-ytd" />
        <Metric label="Realized Losses YTD" value={fmtUSD(summary.realized_losses_ytd)} testId="tax-losses-ytd" />
      </div>
      {summary.underfunded && (
        <div className="panel border-amber-700/60 bg-amber-500/5 px-4 py-3 text-amber-400 text-sm font-semibold" data-testid="tax-underfunded-banner">
          WARNING: Tax reserve coverage is below 100%. Deployable capital is overstated until the reserve is funded.
        </div>
      )}
      <Panel title={`All Tax Events (method: ${summary.accounting_method})`} testId="tax-events-panel">
        {events.length === 0 ? <Empty>No tax events yet.</Empty> : (
          <Table testId="tax-events-table" head={[{ label: "Time" }, { label: "Type" }, { label: "Asset" }, { label: "Qty", num: true }, { label: "FMV", num: true }, { label: "Cost Basis", num: true }, { label: "Proceeds", num: true }, { label: "Gain", num: true }, { label: "Loss", num: true }, { label: "Income", num: true }, { label: "Est. Tax", num: true }, { label: "Treatment" }, { label: "Status" }]}>
            {events.map((e) => (
              <tr key={e.id} className="hover:bg-[#222630]" data-testid={`tax-event-${e.id.slice(0, 8)}`}>
                <td className="td-cell font-mono whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                <td className="td-cell font-semibold">{e.event_type.replace(/_/g, " ")}</td>
                <td className="td-cell">{e.asset}</td>
                <td className="td-num">{fmtQty(e.quantity)}</td>
                <td className="td-num">{fmtUSD(e.fair_market_value)}</td>
                <td className="td-num">{fmtUSD(e.cost_basis)}</td>
                <td className="td-num">{fmtUSD(e.proceeds)}</td>
                <td className="td-num"><Money value={e.realized_gain} tone="pos" /></td>
                <td className="td-num"><Money value={e.realized_loss} tone="neg" /></td>
                <td className="td-num">{fmtUSD(e.income_amount)}</td>
                <td className="td-num text-amber-400">{fmtUSD(e.estimated_total_tax)}</td>
                <td className="td-cell text-slate-500 max-w-[200px] truncate" title={e.tax_treatment}>{e.tax_treatment}</td>
                <td className="td-cell"><StatusBadge status={e.status} /></td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}
